import * as d3 from 'd3';
import { interpolateRdYlGn } from 'd3-scale-chromatic';
import chartTooltip from './d3-tooltip';
import './d3-correlationschart.css';
import { colors, LEGEND_ITEM_RADIUS, getSVGPosition } from '../utils';

function truncate(str) {
  const length = str.length;

  if (length > 16) {
    return str.substring(0, 16) + '…';
  } else {
    return str;
  }
}

export default function () {
  const margin = { top: 10, right: 60, bottom: 20, left: 100 };

  let width = 400;
  let height = 300;
  let svg;
  let tooltip;
  let _data;
  let assets;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let yFontScale;
  let color;
  let colorQuantized;
  let selected;

  const dispatch = d3.dispatch('selected');

  function chart(selection) {
    selection.each(function (data) {
      _data = data;
      assets = data.map(function (d) { return d.Series2; })
      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        g.append('g')
          .attr('class', 'correlations-chart__correlations');

        g.append('g')
          .attr('class', 'correlations-chart__legend');

        createTooltip();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawCorrelations();
      drawLegend();
    });
  }

  function createScales() {
    x = d3.scaleBand().range([0, chartWidth]).padding(0.1);
    y = d3.scaleBand().range([0, chartHeight]).padding(0.1);
    color = d3.scaleSequential(interpolateRdYlGn);
    colorQuantized = d3.scaleQuantize().range(d3.quantize(interpolateRdYlGn, 5));
    yFontScale = d3.scaleLinear().domain([170, 300]).range([7, 10]).clamp(true);

    x.domain(_data.map(function (d) { return d.Series2; }));
    y.domain(_data.map(function (d) { return d.Series2; }));

    color.domain([1, -1]);
    colorQuantized.domain([-1, 1]);
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'correlations-chart__axis correlations-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'correlations-chart__axis correlations-chart__axis--y');
  }

  function createTooltip() {
    tooltip = chartTooltip();
    const tooltipContainer = d3.select('body')
      .append('div')
      .attr('class', 'correlations-chart__tooltip');

    tooltipContainer.datum([]).call(tooltip);
  }

  function drawAxes() {
    const xAxis = svg.select('.correlations-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).tickSize(0))
      .select('.domain').remove();

    xAxis.selectAll('text')
      .attr('dx', '-0.8em')
      .attr('dy', '.35em')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .style('display', 'none');

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    xAxis.selectAll('.tick text')
      .attr('font-size', yFontScale(chartWidth))
      .text(function (d) { return truncate(d); });

    const yAxis = svg.select('.correlations-chart__axis--y')
      .call(d3.axisLeft(y).tickSize(0));

    yAxis.select('.domain').remove(); // Created by d3.axisLeft

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('font-size', yFontScale(chartHeight))
      .attr('x', -6)
      .attr('dy', 4);

    yAxis.selectAll('.tick text')
      .text(function (d) { return truncate(d); });
  }

  function drawCorrelations() {
    const rows = svg.select('.correlations-chart__correlations')
      .selectAll('.row')
      .data(_data);

    const rowsEnter = rows.enter()
      .append('g')
      .attr('class', 'row')
      .merge(rows)
      .attr('transform', function (d) { return 'translate(0,' + y(d.Series2) + ')'; });

    const cells = rowsEnter.selectAll('.cell')
      .data(function (d) { return assets.map(function (a) { return { Series2: d.Series2, key: a, value: d[a] }; }); });

    const cellsEnter = cells.enter()
      .append('g')
      .attr('class', 'cell')
      .merge(cells)
      .attr('transform', function (d) { return 'translate(' + x(d.key) + ',0)'; });

    cellsEnter.append('rect')
      .attr('width', function (d) { return x.bandwidth(); })
      .attr('height', function (d) { return y.bandwidth(); })
      .attr('fill', function (d) { return color(d.value); })
      .on('mouseover', function () {
        tooltip.show();

        const item = d3.select(this);
        const Series2 = item.data()[0].Series2;
        const key = item.data()[0].key;

        item.classed('correlations-chart__legend--highlighted', true);

        svg.selectAll('.correlations-chart__axis--x')
          .selectAll('.tick')
          .classed('correlations-chart__axis--highlighted', function (d) { return d === key; });

        svg.selectAll('.correlations-chart__axis--y')
          .selectAll('.tick')
          .classed('correlations-chart__axis--highlighted', function (d) { return d === Series2; });
      })
      .on('mousemove', function (d) {
        const item = d3.select(this);
        const Series2 = item.data()[0].Series2;
        const key = item.data()[0].key;

        const SVGPos = getSVGPosition(this);
        const offset = 20;

        tooltip.update({ x: SVGPos[0] + offset, y: SVGPos[1] + offset}, [{ key: Series2, value: null }, { key: key, value: null }], d3.format('.2f')(d.value));
      })
      .on('mouseout', function () {
        tooltip.hide();
        d3.select(this).classed('correlations-chart__legend--highlighted', false);

        svg.selectAll('.correlations-chart__axis--x')
          .selectAll('.tick')
          .classed('correlations-chart__axis--highlighted', false);

        svg.selectAll('.correlations-chart__axis--y')
          .selectAll('.tick')
          .classed('correlations-chart__axis--highlighted', false);
      })
      .merge(cells.select('rect'))
      .attr('width', function (d) { return x.bandwidth(); })
      .attr('height', function (d) { return y.bandwidth(); })
      .attr('fill', function (d) { return color(d.value); });
  }

  function drawLegend() {
    const NUMBER_LEGEND_ITEMS = 5;

    const legend = svg.select('.correlations-chart__legend')
      .selectAll('g')
      .data(d3.range(NUMBER_LEGEND_ITEMS).reverse());

    const legendEnter = legend.enter()
      .append('g');

    const SPACE_BETWEEN_LEGEND_ITEMS = 16;

    legend.merge(legendEnter)
      .attr('transform', function (d, i) { return 'translate(' + chartWidth + ',' + i * SPACE_BETWEEN_LEGEND_ITEMS + ')'; });

    legendEnter.append('circle')
      .attr('cx', 10)
      .attr('cy', 9)
      .attr('r', LEGEND_ITEM_RADIUS)
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .merge(legend.select('circle'))
      .attr('cx', 41)
      .attr('fill', function (d, i) { return colorQuantized((i - 2) / 2); });

    legendEnter.append('text')
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .attr('fill', 'black')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'end')
      .merge(legend.select('text'))
      .attr('x', 30)
      .text(function (d) { return (d - 2) / 2; });
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    d3.select('.correlations-chart__tooltip').remove();
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-correlationschart.js